/**
 * EmailKeyboardHandler — Gmail-compatible keyboard shortcuts.
 *
 * Delegates entirely to the emailStore (CSR). No Stimulus outlets / turbo-stream.
 *
 * Shortcuts:
 *   j       → next email in list
 *   k       → previous email in list
 *   e       → archive current thread
 *   s       → star/unstar current thread
 *   r       → reply to current thread
 *   c       → compose new email
 *   /       → focus search input
 *   Escape  → blur search, or close compose
 */

export class EmailKeyboardHandler {
  constructor({ element, store }) {
    this._el    = element
    this._store = store
    this._onKeyDown = this._handleKeyDown.bind(this)
  }

  connect() {
    document.addEventListener('keydown', this._onKeyDown)
  }

  disconnect() {
    document.removeEventListener('keydown', this._onKeyDown)
  }

  _handleKeyDown(e) {
    const key = e.key

    if (key === 'Escape') {
      this._escape(e)
      return
    }

    if (this._isInputFocused()) return

    switch (key) {
      case 'j': e.preventDefault(); this._store.focusNext(); break
      case 'k': e.preventDefault(); this._store.focusPrev(); break
      case 'e': e.preventDefault(); this._archive(); break
      case 's': e.preventDefault(); this._star(); break
      case 'r': e.preventDefault(); this._reply(); break
      case 'c': e.preventDefault(); this._store.openCompose('new'); break
      case '/': e.preventDefault(); this._focusSearch(); break
    }
  }

  _archive() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    this._store.updateEmailState(threadId, 'archive')
  }

  _star() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    this._store.updateEmailState(threadId, 'star')
  }

  _reply() {
    const threadId = this._store.currentThreadId.value
    if (!threadId) return
    this._store.openCompose('reply', threadId)
  }

  _focusSearch() {
    const input = this._el.querySelector('[data-email-search-input]')
    input?.focus()
  }

  _escape(e) {
    const searchInput = this._el.querySelector('[data-email-search-input]')
    if (document.activeElement === searchInput) {
      searchInput.blur()
      return
    }
    if (this._store.composeOpen.value) {
      e.preventDefault()
      this._store.closeCompose()
    }
  }

  _isInputFocused() {
    const el  = document.activeElement
    const tag = el?.tagName?.toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable
  }
}
